/**
 * TypeScript interfaces for Claims feature
 * Frontend data types with Date objects for date fields
 */

/**
 * Claim Episode event type (CLE-2166).
 * Mirrors backend Prisma enum RemitStatusType. Each child event row of an episode
 * has one of these values, classified at parse time from CLP02 + CAS + REF*F8 +
 * CLP07 lifecycle. See docs/specs/CLE-2166-claim-episodes.md §5.
 */
export enum RemitStatusType {
  PRIMARY = "PRIMARY",
  REPROCESS = "REPROCESS",
  REVERSAL = "REVERSAL",
  DUPLICATE_DENIAL = "DUPLICATE_DENIAL",
  PRIMARY_SPLIT = "PRIMARY_SPLIT",
  DENIAL = "DENIAL",
  SECONDARY = "SECONDARY",
}

export const REMIT_STATUS_LABELS: Record<RemitStatusType, string> = {
  [RemitStatusType.PRIMARY]: "Primary",
  [RemitStatusType.REPROCESS]: "Reprocess",
  [RemitStatusType.REVERSAL]: "Reversal",
  [RemitStatusType.DUPLICATE_DENIAL]: "Duplicate Denial",
  [RemitStatusType.PRIMARY_SPLIT]: "Bilateral Companion",
  [RemitStatusType.DENIAL]: "Denial",
  [RemitStatusType.SECONDARY]: "Secondary",
};

export const REMIT_STATUS_COLORS: Record<
  RemitStatusType,
  "info" | "success" | "warning" | "danger" | undefined
> = {
  [RemitStatusType.PRIMARY]: "success",
  [RemitStatusType.REPROCESS]: "info",
  [RemitStatusType.REVERSAL]: "warning",
  [RemitStatusType.DUPLICATE_DENIAL]: undefined,
  [RemitStatusType.PRIMARY_SPLIT]: "info",
  [RemitStatusType.DENIAL]: "danger",
  [RemitStatusType.SECONDARY]: undefined,
};

/**
 * Resolution Status enum
 * Matches backend Prisma enum ResolutionStatus
 */
export enum ResolutionStatus {
  IN_REVIEW = "IN_REVIEW",
  SUBMITTED = "SUBMITTED",
  CLOSED = "CLOSED",
  NO_APPEAL_NEEDED = "NO_APPEAL_NEEDED",
}

/**
 * Resolution history entry
 * Stored in resolutionHistory JSONB array
 */
export interface IResolutionHistoryEntry {
  status: ResolutionStatus;
  notes?: string;
  userId: string;
  userName: string;
  timestamp: string; // ISO 8601
}

/**
 * CLE-2172: response shape from the two bulk update endpoints
 * (`PATCH /claims/bulk/payer` and `PATCH /claims/bulk/resolution-status`).
 *
 * The endpoints return 200 even on partial failure — `successCount` /
 * `failureCount` always sum to the deduped batch size, and `failures` lists the
 * offending claim ids with their error message. `updatedClaims` carries the
 * successfully-updated rows so the client can patch local cache before the
 * tag invalidation refetch fires.
 */
export interface IBulkClaimUpdateResponse {
  successCount: number;
  failureCount: number;
  failures: { claimId: string; error: string }[];
  updatedClaims: IClaim[];
}

/**
 * Resolution status labels for display
 */
export const RESOLUTION_STATUS_LABELS: Record<
  ResolutionStatus | "null",
  string
> = {
  [ResolutionStatus.IN_REVIEW]: "In Review",
  [ResolutionStatus.SUBMITTED]: "Submitted",
  [ResolutionStatus.CLOSED]: "Closed",
  [ResolutionStatus.NO_APPEAL_NEEDED]: "No Appeal Needed",
  null: "Not Reviewed",
};

/**
 * Resolution status colors (PrimeReact Tag severities)
 * Note: PrimeReact Tag only supports 'success' | 'info' | 'warning' | 'danger' | undefined
 */
export const RESOLUTION_STATUS_COLORS: Record<
  ResolutionStatus | "null",
  "danger" | "warning" | "info" | "success" | undefined
> = {
  null: "danger", // Red - Not Reviewed
  [ResolutionStatus.IN_REVIEW]: "warning", // Orange - In Review
  [ResolutionStatus.SUBMITTED]: "info", // Blue - Submitted
  [ResolutionStatus.CLOSED]: "success", // Green - Closed
  [ResolutionStatus.NO_APPEAL_NEEDED]: undefined, // Default gray - No Appeal Needed
};

/**
 * Filter options for resolution status dropdown
 */
export const RESOLUTION_FILTER_OPTIONS = [
  { label: "Not Reviewed", value: "not_reviewed" },
  { label: "In Review", value: "in_review" },
  { label: "All", value: "all" },
] as const;

/**
 * Payment type enum for filtering claims
 * Note: "underpayment" as a business concept remains - this represents claims where payer paid less than expected
 */
export type PaymentType =
  | "all"
  | "underpayment"
  | "overpayment"
  | "correct"
  | "zero_paid";

// Unified filter options - includes both payment types and categories
// Values starting with CATEGORY_ are sent to categories parameter
// Other values are sent to paymentTypes parameter
export const PAYMENT_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: "All Payment Types", value: "all" },
  { label: "Underpayments", value: "underpayment" },
  { label: "Overpayments", value: "overpayment" },
  { label: "Correct Payments", value: "correct" },
  { label: "Zero-Paid Claims", value: "zero_paid" },
  {
    label: "Charges Below Contract Rate",
    value: "CATEGORY_CHARGE_BELOW_CONTRACT_RATE",
  },
  {
    // CLE-2326: data-quality bucket — claims priced from a contract whose effective period
    // doesn't cover the claim's date of service (e.g. a 2024 contract on a 2022 claim). The
    // under/overpayment label is unreliable for these, so they're filtered out of the
    // default underpayment view; users opt in to triage them.
    label: "Effective Year Missing",
    value: "CATEGORY_EFFECTIVE_YEAR_MISSING",
  },
  {
    // CLE-2462: radiology TC-component variant — a false-positive underpayment (payer paid the
    // Technical Component correctly while our fee schedule modeled the global rate). Hidden from
    // the default underpayment worklist; selecting this option opts the hidden claims back in.
    label: "TC Component (radiology)",
    value: "CATEGORY_TC_COMPONENT_VARIANT",
  },
  {
    // CLE-2489: FEHB Medicare rate-cap (RARC N6) — a 65+ patient without Medicare whose FEHB
    // plan is legally capped at the Medicare-allowed amount. The payer paid correctly at the
    // Medicare rate while our fee schedule modeled the commercial rate, so it is a false-positive
    // underpayment. Hidden from the default underpayment worklist; selecting this option opts in.
    label: "Medicare Rate Cap (FEHB N6)",
    value: "CATEGORY_MEDICARE_RATE_CAP",
  },
];

/**
 * Denial status (CLE-2225) — orthogonal to PaymentType. A claim can be both an
 * underpayment and a partial denial. PARTIAL_DENIAL = some lines denied but the
 * payer paid something; FULL_DENIAL = every payable line denied ($0 paid).
 */
export type DenialStatus = "NONE" | "PARTIAL_DENIAL" | "FULL_DENIAL";

// CLE-2563: how the payer reimbursed the claim's implant/carve-out lines.
export type ImplantPaymentClassification =
  | "NONE"
  | "ADDITIONAL"
  | "NOT_ADDITIONAL";

export const DENIAL_STATUS_LABELS: Record<DenialStatus, string> = {
  NONE: "No Denial",
  PARTIAL_DENIAL: "Partial Denial",
  FULL_DENIAL: "Full Denial",
};

// Filter options for the claims-list Denial Status filter. Lowercase values match
// the API (denialStatuses query param).
export const DENIAL_STATUS_FILTER_OPTIONS: { label: string; value: string }[] =
  [
    { label: "Partial Denial", value: "partial_denial" },
    { label: "Full Denial", value: "full_denial" },
    { label: "No Denial", value: "none" },
  ];

// CLE-2489: Full claim classification taxonomy (code → title), mirroring the
// backend deterministic classifier's CLASSIFICATION_TAXONOMY (the authoritative
// source in services/clear-api/src/domains/claims/utils/deterministic-classifier.util.ts).
//
// The Denial Classification filter (DENIAL_CLASSIFICATION_FILTER_OPTIONS below)
// is DERIVED from this map so it can never again list a strict subset.
// Claim.denialClassificationCode carries the claim's classification code whether
// or not it is a denial. A denied claim (denialStatus PARTIAL/FULL) can carry ANY
// taxonomy code — including Underpayment-group codes such as B6 (CMS NCCI / MUE /
// SI Bundling) — via extent-based denial (CLE-2399). CLE-2533 additionally persists
// the classification on a NON-denial (denialStatus NONE) when the classifier
// recognized an Underpayment-group code (e.g. B10 "Code Downgrade" on a paid
// downcode), so the column + this filter now surface classifications on
// underpayments too. denialStatus remains the authoritative denial signal.
//
// Keep declaration order aligned with the backend taxonomy (category A→Z, code
// ascending) so the dropdown reads naturally (A1, A2, … B1, B2, … B10, B11 …).
export const CLAIM_CLASSIFICATION_TITLES: Record<string, string> = {
  // A — Rate & Fee Schedule
  A1: "Wrong Fee Schedule Applied",
  A2: "Medicare / MA Rate Applied Instead of Commercial",
  A3: "Percent-of-Charges Pricing",
  A4: "Charge Below Contract Rate",
  // B — Methodology
  B1: "MPPR Reduction Misapplied (Payer)",
  B2: "MPPR Reduction Misapplied (provider)",
  B3: "Bilateral Billing Modifier Issue (payer)",
  B4: "Bilateral Billing Modifier Issue (provider)",
  B5: "Case Rate / Episode Bundling Mismatch",
  B6: "Bundling / Packaging Rule Misapplied (NCCI/MUE/SI or payer packaging)",
  B7: "Place-of-Service (POS) Pricing Mismatch",
  B8: "Pass-Through Drug / Separately Payable Supply Divergence",
  B9: "Readmission Misapplied",
  B10: "Code Replacement / Downgrade",
  B11: "Unlisted → Similar Code Assigned",
  B12: "Add-On Code Removed",
  B13: "Payer Auto-Adds Modifier",
  B14: "Incorrect / Invalid Code or Split Billing Pattern",
  B15: "Transfer Methodology Misapplied",
  B16: "Cost Outlier Payment Not Applied",
  B17: "Packaging / Bundling Logic Mismatch", // CLE-2569 DEPRECATED: merged into B6; kept so legacy rows still render.
  // C — Remit Completeness / Pass-Through
  C1: "Medicare IME Pass-Through (Not the DRG Payment)",
  C2: "Incomplete Remit History / Awaiting Prior Remit",
  // D — Coverage Policy
  D1: "Medical Necessity / LCD / NCD",
  D2: "Benefit Exhaustion / Max Limit",
  D3: "Non-Covered / Excluded Service",
  D4: "Clinical Criteria / Indication Not Met",
  D5: "Patient Liability / ABN / No Appeal",
  // E — Authorization
  E1: "Prior Authorization Failure",
  E2: "Referral Missing / Invalid",
  // F — COB / Eligibility
  F1: "Coordination of Benefits (COB) Issue",
  F2: "Wrong Payer / Benefit Plan Routing",
  F3: "Eligibility / Coverage Not Active",
  // G — Provider
  G1: "Provider Credentialing / Network / Type Issue",
  // H — Operational
  H1: "Documentation / Attachment Required",
  H2: "Missing Information / Structured Claim Data",
  // I — Operational (Claim Construction)
  I1: "Claim Construction / Split Billing / Overlap",
  // J — Timing
  J1: "Frequency / Timing Conflict",
  J2: "Duplicate Claim",
  // K — Implant / Routing
  K1: "Implant / Payment Routed to Different Entity",
  // L — Special / Legal
  L1: "Workers Comp / Liability / Special Program",
  // Z — Zero Pay
  Z1: "Zero Pay Without Explanation",
};

// CLE-2320 / CLE-2489: Options for the claims-list Denial Classification filter.
// Derived from the full taxonomy above; values are the raw code (matches
// Claim.denialClassificationCode).
export const DENIAL_CLASSIFICATION_FILTER_OPTIONS: {
  label: string;
  value: string;
}[] = Object.entries(CLAIM_CLASSIFICATION_TITLES).map(([value, title]) => ({
  label: `${value} — ${title}`,
  value,
}));

export interface IMissingCode {
  code: string;
  quantity: number;
}

export interface ILineItemAdjustment {
  group: string;
  code: string;
  amount: number;
}

export interface ILineItemReference {
  qualifier: string;
  value: string;
}

export interface ILineItemRemark {
  qualifier: string;
  code: string;
}

export interface ILineItem {
  cptCode: string;
  lineProcedureCode: string | null;
  modifier: string | null;
  revenueCode: string | null;
  lineControlNumber: string | null;
  quantity: number;
  billedUnits: number;
  lineCharge: number;
  lineAllowed: number;
  linePaid: number;
  lineTotalAdjAmount: number;
  patientResponsibility?: number; // Sum of all PR adjustments for this line
  adjustments: ILineItemAdjustment[];
  references: ILineItemReference[];
  remarks: ILineItemRemark[];
}

/**
 * Payer type enum matching Prisma schema
 */
export type PayerType =
  | "UhcMedicareAdvantage"
  | "OriginalMedicare"
  | "Commercial"
  | "Medicaid";

/**
 * Matched Order (estimate) data from claims-to-estimates matching
 */
export interface IMatchedOrder {
  id: string;
  dateOfService: string; // ISO 8601
  amountInCents: number;
  serviceDetails: any;
  benefitDetails: any;
  patient: {
    firstName: string;
    lastName: string;
  };
  payer?: {
    type: PayerType | null;
    name?: string;
  } | null;
  gfeQuote?: {
    remainingDeductibleInCents: number | null;
    outOfPocketMaxInCents: number | null;
    flatCopayInCents: number | null;
    coinsurancePercent: number | null;
    pricingBreakdown: any;
  } | null;
}

export interface IEdi837ServiceLine {
  code: string;
  revenueCode?: string;
  modifier?: string;
  quantity: number;
  chargeAmount?: number;
}

export interface IMatched837Claim {
  claimNumber: string;
  chargeAmount: number;
  typeOfBill: string | null;
  placeOfService: string | null;
  patientClass: "Inpatient" | "Outpatient" | "Unknown";
  payerClaimControlNumber: string | null;
  patientName: string | null;
  serviceDate: string | null;
  providerNPI: string | null;
  drgCode: string | null;
  caseSignature: string | null;
  frequencyCode: string;
  isAuthoritative: boolean;
  // Optional at runtime: the 835<->837 matching/reprocessing pipeline (CLE-2483) can
  // leave a matched 837 claim with no service lines, so this array may be absent
  // (undefined) rather than empty. Consumers must guard before reading .length.
  serviceLines?: IEdi837ServiceLine[];
}

// Inpatient Outlier — composable trigger/formula/mode rule engine (CLE-2120 v3).
// Mirrors the services-side shapes in inpatient-outlier.util.ts so the SPA can
// render the audit trail on the Expected Amounts tab.
//
// As of CLE-2120 follow-up (services PR #2942), the stored trigger and formula
// objects include BOTH the runtime evaluation result AND the original rule
// config (operator, days, caseRateInCents, includedDays, losAdjustment, etc.).
// All config fields are marked optional here so older stored rows still parse.
export interface IInpatientOutlierTriggerResult {
  type: string;
  fired: boolean;
  // Runtime evaluation result
  resolvedThresholdInCents?: number;
  effectiveChargesInCents?: number;
  actualLos?: number | null;
  resolvedDays?: number | null;
  matchedDischargeStatus?: string | null;
  matchedCode?: string | null;
  notes?: string;
  // Original rule config (added by services PR #2942 / CLE-2120 follow-up).
  // STANDARD/MODIFIED CHARGE_THRESHOLD:
  thresholdMethod?: "FIXED" | "MULTIPLIER" | "BASE_PLUS_FIXED";
  baseReference?: "BASE_PAYMENT" | "RAW_BASE_PAYMENT";
  fixedAmountInCents?: number;
  multiplier?: number;
  chargeFloorInCents?: number;
  costToChargeRatio?: number; // MODIFIED_CHARGE_THRESHOLD only
  // LOS_RANGE:
  operator?: "GT" | "GTE" | "LT" | "LTE" | "EQ";
  days?: number;
  daysSource?: "MANUAL" | "FEE_SCHEDULE";
  // PER_CODE_CHARGE_THRESHOLD:
  codeChargeFloorInCents?: number;
  onlyCodesWithoutFeeSchedule?: boolean;
  // DISCHARGE_STATUS:
  codes?: string[];
  // ADJUSTMENT_CODE (CLE-2303): config carries the configured CARC codes and
  // the optional dominance gate; runtime carries which codes actually matched
  // and the summed dollar amount used for the dominance check.
  requireDominancePercent?: number | null;
  matchedAdjustmentCodes?: string[];
  matchedAdjustmentAmountInCents?: number;
}

export interface IInpatientOutlierFormulaResult {
  type: string;
  calculatedAmountInCents: number | null;
  // Runtime calculation result
  perDiemInCents?: number;
  excessDays?: number;
  effectiveChargesInCents?: number;
  thresholdInCents?: number;
  flatRateInCents?: number;
  percent?: number;
  reason?: string;
  // Original rule config (added by services PR #2942 / CLE-2120 follow-up).
  // PER_DIEM_X_LOS / PER_DIEM_X_EXCESS_LOS:
  perDiemSource?:
    | "MANUAL"
    | "CASE_RATE_DIV_ALOS_TIMES_MULTIPLIER"
    | "CASE_RATE_DIV_ACTUAL_LOS"
    | "CASE_RATE_DIV_ALOS";
  manualPerDiemInCents?: number;
  losAdjustment?: "NONE" | "PLUS_1" | "MINUS_1";
  capAtDrgRate?: boolean;
  excessLosBase?: number;
  excessLosBaseSource?: "MANUAL" | "FEE_SCHEDULE";
  // CASE_RATE_PLUS_PER_DIEM:
  caseRateInCents?: number;
  includedDays?: number;
  // DRG_FIELD_SUM (CLE-2303): the configured ProviderProcedure breakdown
  // fields summed by this formula (keys from DRG_BREAKDOWN_FIELD_KEYS).
  fields?: string[];
}

export interface IInpatientOutlierPerClaimRule {
  inpatientRulesetId: string;
  rulesetName: string;
  mode: "ADDITIVE" | "MAX" | "REPLACE";
  fired: boolean;
  trigger: IInpatientOutlierTriggerResult;
  formula: IInpatientOutlierFormulaResult;
}

export interface IInpatientOutlierPerCodeRule {
  inpatientRulesetId: string;
  rulesetName: string;
  mode: "ADDITIVE" | "MAX" | "REPLACE";
  fired: boolean;
  // Per-code rule trigger carries the trigger's config alongside its type. The runtime
  // evaluation happens per-line (in `lines`), so the trigger object itself only holds
  // type + config — no claim-level fired/effective values.
  trigger: {
    type: "PER_CODE_CHARGE_THRESHOLD";
    codeChargeFloorInCents?: number;
    onlyCodesWithoutFeeSchedule?: boolean;
  };
  // Formula carries config used by per-line calculation (e.g. percent for
  // PERCENT_OF_CHARGES, flatRateInCents for FLAT_RATE).
  formula:
    | IInpatientOutlierFormulaResult
    | { type: string; [key: string]: unknown };
  totalCalculatedAmountInCents: number;
  lines: {
    code: string;
    chargeAmountInCents: number;
    fired: boolean;
    calculatedAmountInCents: number | null;
    reason?: string;
  }[];
}

export type IInpatientOutlierRule =
  | IInpatientOutlierPerClaimRule
  | IInpatientOutlierPerCodeRule;

export interface IInpatientOutlierCalculation {
  rules: IInpatientOutlierRule[];
  aggregateAdditiveInCents: number | null;
  aggregateMaxInCents: number | null;
  replaceInCents: number | null;
}

// Lesser-of-charges cap — runs after inpatient rulesets to cap the final expected
// at billed charges × multiplier. Scope decides whether the cap is evaluated against
// the claim's total charges or per service line.
export type LesserOfChargesScope = "LINE_CHARGES" | "TOTAL_CHARGES";

// One entry per line the cap actually changed (fired lines only — the backend
// omits non-targeted and unchanged lines).
export interface ILesserOfChargesPerLineCap {
  cptCode: string;
  cappedExpectedInCents: number;
}

export interface ILesserOfChargesCalculation {
  appliedRulesetId: string;
  ruleName: string | null;
  scope: LesserOfChargesScope;
  multiplier: number;
  fired: boolean;
  originalExpectedInCents: number;
  cappedExpectedInCents: number;
  totalChargeInCents: number;
  // TOTAL_CHARGES only — sum of charges from lines whose CPT participates in
  // the rule (in `codes` and not in `excludedCodes`). The cap denominator.
  includedChargeInCents?: number;
  capInCents?: number;
  perLineCaps?: ILesserOfChargesPerLineCap[];
}

// ChargeBasedPricing — a separate rule domain (NOT a mode on lesser-of-charges).
// When this fires, the rule REPLACES the standard pricing pipeline entirely:
// expected = multiplier × charges. Used for contracts that price purely as a
// percentage of charges with no fee schedule.
export type ChargeBasedPricingScope = "LINE_CHARGES" | "TOTAL_CHARGES";

// One entry per priced line (LINE_CHARGES scope only). Lines without a CPT are
// dropped by the backend and not represented here.
export interface IChargeBasedPricingPerLineCalculation {
  cptCode: string;
  pricedExpectedInCents: number;
}

/**
 * CLE-2836 — audit trail for the global-period PRICING ADJUSTMENT, written on the episode root by
 * recomputeEpisode. It is an AUDIT RECORD, not a UI surface (CLE-2836 removed the panel): it
 * records WHY a $0 professional line is not an underpayment — an adjudicated
 * surgery for the same patient opened a CMS global period that covers this claim's date of service.
 *
 * The adjustment HAS been applied to the money: the covered line is priced at $0 inside the pricing
 * engine, at the same level as a packaged code. `neutralizedExpectedInCents` is the amount priced
 * away from the bundled line(s) themselves, and `residualExpectedInCents` is what the claim
 * actually expects afterwards. The two need not sum to the old expected: zeroing a line can also
 * re-rank the survivors (MPDR), and the residual is the authoritative number. `expectedAmount`, `amountDifference` and `paymentType` all reflect it.
 */
export interface IGlobalPeriodCalculation {
  ruleVersion: number;
  /** N525 / M144 are the global-period RARCs; CO97_ONLY is a clean CO-97 (or CO-B15) bundle. */
  trigger: "N525" | "M144" | "CO97_ONLY";
  confidence: "HIGH" | "MEDIUM";
  /** How the parent relates to the denied line: same CPT body system, or the explicit postop map. */
  relatedness: "BODY_SYSTEM" | "POSTOP_MAP";
  childLines: {
    cptCode: string;
    modifier: string | null;
    neutralizedExpectedInCents: number;
  }[];
  neutralizedExpectedInCents: number;
  residualExpectedInCents: number;
  parentClaimId: string;
  parentClaimNumber: string | null;
  /** Parent date of service, YYYY-MM-DD. */
  parentDos: string;
  /** The parent line that set the session's global period (the longest one paid that day). */
  parentCpt: string;
  parentGlobalDays: number;
  daysSinceParent: number;
}

export interface IChargeBasedPricingCalculation {
  appliedRulesetId: string;
  ruleName: string | null;
  scope: ChargeBasedPricingScope;
  multiplier: number;
  // The final number the rule produced; equals claim.expectedAmount when this
  // rule fires (the pipeline replaces the standard expected with this value).
  // When capApplied=true, equals maxAmountInCents; otherwise equals rawExpectedInCents.
  pricedExpectedInCents: number;
  // Pre-cap expected = multiplier × includedChargeInCents. Equals pricedExpectedInCents
  // when no cap is set or the cap did not fire.
  rawExpectedInCents: number;
  // The ruleset's optional ceiling at the time of pricing. Null = no cap configured.
  maxAmountInCents: number | null;
  // True only when maxAmountInCents was set AND it reduced the expected.
  capApplied: boolean;
  totalChargeInCents: number;
  // Wildcard TOTAL → equals totalChargeInCents; code-filtered TOTAL → sum of
  // participating-line charges; LINE → sum of priced-line charges.
  includedChargeInCents: number;
  // CLE-2627 — billed charge on the lines this contract packaged to $0. Absent on every
  // claim priced by a rule with no packaged codes, so consumers must tolerate undefined.
  packagedChargeInCents?: number;
  // LINE_CHARGES only — the charge-based lines. Superseded by `perLines`; kept so
  // claims priced before CLE-2546 still render.
  perLineCaps?: IChargeBasedPricingPerLineCalculation[];

  /* ── CLE-2546 ── */
  // Every ruleset that priced at least one line (a code rule + a catch-all can both fire).
  appliedRulesetIds?: string[];
  // Full per-line breakdown, including lines that kept their fee-schedule price.
  perLines?: IChargeBasedPricingLineDetail[];
  // Expected retained from the standard pipeline for lines no rule repriced.
  feeScheduleRetainedInCents?: number;
  // True when charge-based rules owned every line, so nothing upstream survived.
  fullyOwned?: boolean;

  /* ── CLE-2600 — Contract Hierarchy Rules ── */
  // The ContractHierarchyRule whose suppressions changed this claim's pricing. Both are
  // absent unless a directive actually changed the money, so a claim no hierarchy rule
  // touched carries neither.
  hierarchyRuleId?: string;
  hierarchyRuleName?: string;
}

// CLE-2546 — one entry per claim line, whichever way it was priced.
export interface IChargeBasedPricingLineDetail {
  cptCode: string | null;
  chargeInCents: number;
  // What the pipeline held for this line going into the override — post lesser-of cap.
  feeScheduleExpectedInCents: number;
  pricedExpectedInCents: number;
  // CHARGE_BASED = a charge-based rule priced this line; FEE_SCHEDULE = it kept the
  // standard pipeline's price (no rule owned it, or a fill-the-gap rule stood down);
  // BUNDLED (CLE-2600) = a PACKAGE or CASE_RATE ruleset zeroed it on purpose, so a
  // fill-the-gap rule stood down and the line stays at $0. Claims priced before
  // CLE-2600 never carry BUNDLED.
  // CLE-2627 — "PACKAGED": the charge-based rule itself priced this line at $0 because the
  // payer never reimburses the code separately. Distinct from "BUNDLED", which is the FEE
  // SCHEDULE's decision (a PACKAGE / CASE_RATE ruleset) and carries suppressedBy* fields.
  source: "CHARGE_BASED" | "FEE_SCHEDULE" | "BUNDLED" | "PACKAGED";
  // The CHARGE-BASED ruleset that priced this line. Null for FEE_SCHEDULE and BUNDLED.
  rulesetId: string | null;
  ruleName: string | null;
  multiplier: number | null;
  maxAmountInCents: number | null;
  // True only when THIS line's cap reduced it — the cap is per line item.
  capApplied: boolean;

  /* ── CLE-2600 — source === "BUNDLED" only ── */
  // The PACKAGE / CASE_RATE ruleset that bundled the line. Deliberately separate from
  // `rulesetId`, which is resolved against charge-based rule names.
  suppressedByRulesetId?: string | null;
  suppressedByRuleType?: string | null;
  // The bundling adjustment's description, e.g. "Packaged Code — included in the panel".
  suppressedByDescription?: string | null;

  /* ── CLE-2600 — Contract Hierarchy Rules ── */
  // A hierarchy PAY_BOTH rule let the owning charge-based rule price through the trigger
  // ruleset's bundling, so this line was charge-filled instead of standing down. Only ever
  // `true`; absent on every line the hierarchy did not touch.
  hierarchyOverridden?: boolean;
}

export interface IClaim {
  id: string;
  patientName: string;
  patientDOB: Date | null;
  patientAccountNumber: string | null;
  claimNumber: string | null;
  claimPayerId: string | null;
  claimPayerName: string;
  claimPayerGroupNumber: string | null;
  claimPlanName: string | null;
  claimNetworkStatus: string | null;
  clearPayerName: string | null;
  clearParentPayerName: string | null;
  payerId: number | null; // Numeric payer ID for fetching allowables
  // CLE-2500: the mapped Provider (denormalized onto every claim row). Drives pricing
  // and is editable from the claim modal, mirroring the matched-payer control.
  providerId?: string | null;
  accountId?: string; // Owning account — scopes the provider picker to same-account providers
  serviceDate: Date;
  chargeAmount: number | null; // Display in dollars (divide by 100)
  expectedAmount: number | null; // Display in dollars (divide by 100) — already reduced when CLE-2295 sequestration applies; the pre-sequestration value is expectedAmount + sequestrationDiscountInCents
  claimAmount: number; // Display in dollars (divide by 100)
  totalPaid: number | null; // Insurance-paid-only amount in cents (divide by 100); $0 = denial
  amountDifference: number | null; // Display in dollars (divide by 100)
  // CLE-2295/2534: Medicare/MA sequestration discount applied to expectedAmount, in cents —
  // the remit's own summed CO-253, not a configured percent, so it varies per claim.
  // Null when it didn't apply (no CO-253, a reversal netting <= 0, or 253 >= expected).
  sequestrationDiscountInCents?: number | null;
  // CLE-2563: implant payment accounting. Implant lines are excluded from claimAmount/expectedAmount,
  // so these surface the payer's implant reimbursement. remitImplantAllowedInCents is the value shown
  // in the "Remit Implant Amount" column — but only when implantPaymentClassification is ADDITIONAL.
  // NOT_ADDITIONAL means a bundled implant closed an apparent underpayment (paymentType was flipped to
  // CORRECT). Amounts are in cents (divide by 100); null when the claim has no implant lines.
  remitImplantAllowedInCents?: number | null;
  remitImplantPatientResponsibilityInCents?: number | null;
  implantPaymentClassification?: ImplantPaymentClassification | null;
  caseSignature: string;
  paymentType?: "UNDERPAYMENT" | "OVERPAYMENT" | "CORRECT" | "UNKNOWN" | null;
  denialStatus?: DenialStatus | null; // CLE-2225: denial classification, orthogonal to paymentType
  // CLE-2225: the denial classification (code/title/reason) that produced denialStatus.
  // Null for non-denials; reason is surfaced as a hover tooltip in the claim modal.
  denialClassificationCode?: string | null;
  denialClassificationTitle?: string | null;
  denialClassificationReason?: string | null;
  lineItems?: ILineItem[]; // Line-level details from EDI file
  category: string | null; // Categorizes type of payment discrepancy
  patientClass?: "INPATIENT" | "OUTPATIENT" | "UNKNOWN" | null;
  // Raw CLP08 from 835 — a Type-of-Bill prefix on institutional claims, a CMS Place-of-Service
  // code on professional claims. Interpret via claimFormType (e.g., "11", "13", "83", "24").
  facilityTypeCode?: string | null;
  // INSTITUTIONAL (UB-04 facility claim) vs PROFESSIONAL (CMS-1500) — CLE-2235
  claimFormType?: "INSTITUTIONAL" | "PROFESSIONAL" | "UNKNOWN" | null;
  drgCode?: string | null; // CLP11 from 835 - DRG code for inpatient claims (e.g., "807")
  providerNPI?: string | null; // NPI from N1*PE in 835 — billing/group NPI
  renderingProviderNPI?: string | null; // NM1*82 rendering provider NPI (CLE-2437 specialty routing)
  renderingTaxonomy?: string | null; // PRV*PE*PXC rendering taxonomy — the specialty signal the 835 lacks
  renderingProviderName?: string | null; // CLE-2559 — NPPES name for renderingProviderNPI ("First Last, MD")
  billingProviderName?: string | null; // CLE-2559 — NPPES name for providerNPI (usually the org name)
  typeOfBill?: string | null; // 3-digit TOB — from 837, or reconstructed as CLP08‖CLP09 (e.g., "131", "111")
  aiAnalysisResult?: IAIAnalysisResult | null; // AI analysis of claim
  differenceAnalysis?: IDifferenceAnalysisResult | null; // AI difference analysis vs similar claims
  promptVersion?: IPromptVersionSummary; // Prompt version used for AI analysis
  resolutionStatus: ResolutionStatus | null; // Current resolution status
  resolutionHistory?: IResolutionHistoryEntry[]; // Audit trail of status changes
  ediConversionFile?: {
    fileName: string;
    displayName: string;
    createdAt: Date;
  };
  precalculatedAllowable?: {
    id: string;
    calculatedData: any;
    allowableAccuracyStatus: boolean;
    missingCodes: IMissingCode[];
  };
  // 837 Claim Submission Data (Feature CLE-1878)
  matched837Claims?: IMatched837Claim[] | null;
  edi837CaseSignature?: string | null;
  edi837ExpectedAmount?: number | null;
  // DRG LOS/Transfer Pricing (CLE-1885, CLE-1904)
  dischargeStatus?: string | null;
  losOutlierCalculation?: {
    pricingRule: string;
    ruleApplied: boolean;
    ruleStatus: string;
    drgBaseRateInCents: number;
    alos: number | null;
    los: number | null;
    losSource: string | null;
    // LOS outlier fields
    threshold?: number | null;
    multiplier?: number;
    outlierDays?: number;
    perDiemInCents?: number;
    outlierAddOnInCents?: number;
    expectedAllowedInCents?: number;
    // Transfer fields
    dischargeStatus?: string;
    transferPaymentInCents?: number;
    // DRG code used for pricing
    drgCode?: string;
  } | null;
  // High-Cost Outlier (CLE-1886, CLE-1903)
  isHighCostOutlier?: boolean | null;
  outlierPaymentInCents?: number | null;
  outlierCalculation?: {
    inpatientRulesetId: string;
    type: string;
    costToChargeRatio: number;
    fixedLossThresholdInCents: number;
    outlierPercent: number;
    estimatedCostInCents: number;
    triggerAmountInCents: number;
  } | null;
  // Stop Loss (CLE-1897)
  isStopLoss?: boolean | null;
  stopLossPaymentInCents?: number | null;
  stopLossCalculation?: {
    inpatientRulesetId: string;
    type: string;
    thresholdMultiplier: number;
    paymentPercent: number;
    thresholdAmountInCents: number;
    chargeAmountInCents: number;
  } | null;
  // Charge Outlier (CLE-2120)
  isChargeOutlier?: boolean | null;
  chargeOutlierPaymentInCents?: number | null;
  chargeOutlierCalculation?: {
    inpatientRulesetId: string;
    type: string;
    thresholdType: string;
    thresholdAmountInCents: number;
    outlierPercent: number;
    chargeFloorInCents?: number | null;
    chargeAmountInCents: number;
    edi837RawBaseExpected: number;
    excessChargesInCents?: number;
    chargeOutlierPaymentInCents?: number;
  } | null;
  // Inpatient Outlier — composable trigger/formula/mode rule engine (CLE-2120 v3)
  isInpatientOutlier?: boolean | null;
  inpatientOutlierPaymentInCents?: number | null;
  inpatientOutlierAdditiveInCents?: number | null;
  inpatientOutlierMaxInCents?: number | null;
  inpatientOutlierReplaceInCents?: number | null;
  inpatientOutlierCalculation?: IInpatientOutlierCalculation | null;
  // Lesser-of-charges cap (post-pricing) — caps expected at billed charges × multiplier
  lesserOfChargesCalculation?: ILesserOfChargesCalculation | null;
  // Charge-Based Pricing — when fired, REPLACES the standard pricing (fee schedule,
  // inpatient outliers, stop-loss, lesser-of cap). Mutually exclusive with the
  // standard pricing pipeline; if this is set, the rule alone determines the expected.
  chargeBasedPricingCalculation?: IChargeBasedPricingCalculation | null;
  // CLE-2836 - audit trail for the global-period adjustment: which prior surgery's global period
  // explains the $0 line(s). Null on every claim the rule did not fire on (nearly all of them).
  // Returned by the API and kept for diagnostics; the SPA does not render it.
  globalPeriodCalculation?: IGlobalPeriodCalculation | null;
  passThroughCalculation?: {
    type: string;
    totalPassThroughAmountInCents: number;
    codes: {
      code: string;
      inpatientRulesetId: string;
      chargeMultiplier: number;
      thresholdInCents: number;
      chargeInCents: number;
      calculatedAmountInCents: number;
    }[];
  } | null;
  edi837PrecalculatedAllowable?: {
    id: string;
    calculatedData: any;
    allowableAccuracyStatus: boolean;
    missingCodes: IMissingCode[];
  } | null;
  // Order matching - linked estimate
  orderId?: string | null;
  orderMatchConfidence?: number | null; // 0.0-1.0
  orderMatchedAt?: string | null; // ISO 8601
  order?: IMatchedOrder | null;
  // Variance analysis - root causes for claim vs estimate discrepancies
  varianceAnalysis?: IVarianceAnalysis | null;
  // Claim-level summary data for inpatient claims (no SVC lines)
  claimSummaryData?: {
    adjustments?: Array<{ group: string; code: string; amount: number }>;
    coveredAmount?: number | null;
    drgWeight?: string | null;
    drgAmount?: number | null;
    coveredDays?: number | null;
    lengthOfStay?: number | null;
  } | null;
  // NCCI Bypass - per-claim toggle for NCCI edit bypass.
  // ncciBypassMode is the source of truth (AUTO | FORCED_ON | FORCED_OFF); ncciBypassEnabled
  // mirrors FORCED_ON. ncciBypassEffective is the resolved on/off the toggle should show
  // (FORCED_ON, or AUTO that auto-detected a bypass) — surfaced by the by-id claim fetch.
  ncciBypassEnabled?: boolean;
  ncciBypassMode?: "AUTO" | "FORCED_ON" | "FORCED_OFF";
  ncciBypassEffective?: boolean;
  // Manual Payer Override - indicates payer was manually set by user
  isPayerManuallySet?: boolean;
  // CLE-2166 Episode model
  isEpisodeRoot?: boolean;
  eventCount?: number | null; // Populated on episode rows; child count
  childEvents?: IClaimEvent[]; // Present on getClaimById response for episode rows
  episodeNetLineItems?: ILineItem[] | null; // Cached post-aggregation lines for the Expected Amounts tab
  patientIdentityHash?: string | null;
  // CLE-2628 Claim groups: separate claim numbers priced as one encounter. The list shows only the
  // primary; `combinedClaimNumbers` lists the member claim numbers folded into it (empty when the
  // row is not a group primary).
  isClaimGroupPrimary?: boolean;
  claimGroupId?: string | null;
  combinedClaimNumbers?: string[];
}

/**
 * One claim event under an episode (CLE-2166). Returned as `childEvents` on the
 * episode-detail response. Each event row corresponds to one CLP segment from a
 * source 835 file.
 */
export interface IClaimEvent {
  id: string;
  remitStatusType: RemitStatusType | null;
  remitStatusCode: string | null;
  payerClaimControlNumber: string | null;
  bprPaymentDate: string | null; // ISO 8601 date
  supersedesPayerClaimControlNumber: string | null;
  supersedesClaimId: string | null;
  caseSignature: string;
  chargeAmount: number | null;
  claimAmount: number;
  totalPaid: number | null;
  lineItems: ILineItem[] | null;
  claimSummaryData: IClaim["claimSummaryData"];
  ediConversionFile?: {
    fileName: string;
    displayName: string;
    createdAt: Date;
  } | null;
}

/**
 * AI Analysis Result interfaces
 */
export interface IAIAnalysisResult {
  analyzedAt: string; // ISO 8601 timestamp
  model: string; // OpenAI model used
  summary: string; // High-level summary of the claim analysis
  effectiveTotals: IAIEffectiveTotals;
  excludedImplants: IAIExcludedImplant[];
  lineItemDiscrepancies: IAILineItemDiscrepancy[];
  classifications: IAIClassification[];
  resolution: IAIResolution;
  variance: IVarianceCalculation;
  action?: string | null; // Recommended action: "appeal", "reprocess", "reprocess with correct modifier", "no appeal", etc.
}

export interface IAIEffectiveTotals {
  originalExpectedAllowed: number; // cents
  originalActualAllowed: number; // cents
  excludedImplantExpected: number; // cents
  excludedImplantActual: number; // cents
  effectiveExpectedAllowed: number; // cents
  effectiveActualAllowed: number; // cents
  underpaymentAmount: number; // cents - business concept: how much payer underpaid
}

export interface IAIExcludedImplant {
  cpt: string;
  modifier: string | null;
  revenueCode: string;
  expectedAllowed: number; // cents
  actualAllowed: number; // cents
  reason: string;
}

export interface IAILineItemDiscrepancy {
  cpt: string;
  modifier: string | null;
  expectedAllowed: number; // cents
  actualAllowed: number; // cents
  adjustmentCodes: string[];
  explanation: string;
}

export interface IAIClassification {
  code: string; // e.g., "C5", "D2"
  category: string; // e.g., "Contract / Calculation Errors"
  title: string; // e.g., "MPPR Error"
  confidence: "High" | "Medium" | "Low";
  explanation: string;
}

export interface IAIResolution {
  hasRealDiscrepancy: boolean;
  appealLetterDraft: string;
}

export interface IVarianceCalculation {
  expectedAllowed: number; // cents
  actualAllowed: number; // cents
  variance: number; // cents
  percentDifference: number; // percentage
}

export interface IClaimFilters {
  accountId: string; // Required
  search?: string;
  startDate?: Date;
  endDate?: Date;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  pageSize?: number;
  minDifferencePercent?: number; // Minimum difference percentage filter
  maxDifferencePercent?: number; // CLE-2565: maximum difference percentage filter (upper bound)
  minDifferenceAmount?: number; // CLE-2512: minimum absolute dollar difference — matches claims where |amountDifference| >= this many dollars
  chargeBelowContractRateFilter?: "include" | "exclude" | "only";
  effectiveYearMissingFilter?: "include" | "exclude" | "only"; // CLE-2326: filter claims priced off a contract that doesn't cover their DOS
  categories?: string[]; // Array of category values to filter by (e.g., 'CHARGE_BELOW_CONTRACT_RATE')
  underpaymentOnly?: boolean; // When true, only show records where amountDifference < 0 (true underpayments) - DEPRECATED: use paymentTypes instead
  paymentTypes?: string[]; // Array of payment types to filter by (underpayment, overpayment, correct, zero_paid, all)
  denialStatuses?: string[]; // CLE-2225: denial statuses to filter by (none, partial_denial, full_denial); orthogonal to paymentTypes
  denialClassificationCodes?: string[]; // CLE-2320: filter by Claim.denialClassificationCode (e.g., "D3", "E1"); orthogonal to denialStatuses
  renderingTaxonomies?: string[]; // CLE-2489: filter by Claim.renderingTaxonomy (rendering-provider specialty code, e.g., "2084P0800X"); rolled up to episode root under CLE-2437
  resolutionFilter?: "not_reviewed" | "in_review" | "all"; // Filter by resolution status
  classificationFilter?: string; // AI classification code filter (e.g., "C5", "D2") or "not_analyzed" or "all"
  payerIdFilter?: string; // Filter by Clear payer id (numeric id as string) or 'no-match'
  payerIdFilters?: string[]; // CLE-2565: multi-select Clear Payer filter (numeric id strings and/or 'no-match')
  patientClassFilter?: "inpatient" | "outpatient" | "all"; // Filter by patient class
  claimCategoryFilter?:
    | "institutional"
    | "professional-fac"
    | "professional-nonfac"
    | "anesthesia"
    | "all"; // CLE-2236 follow-up: filter by claim form type + place of service
  hasMatchedOrder?: boolean; // When true, only show claims with a matched order (estimate insights)
  minEventCount?: number; // CLE-2166: filter episodes by minimum child event count
  maxEventCount?: number;
  ediConversionFileIds?: string[]; // CLE-2491: filter to claims from specific EDI conversion file(s)
}

export interface IClaimResponse {
  data: IClaim[];
  totalCount: number;
  page: number;
  pageSize: number;
  message?: string;
}

// Category constants (match backend enum values).
//
// CHARGE_BASED_PRICING_LINE / CHARGE_BASED_PRICING_TOTAL are DEPRECATED — they were
// emitted briefly while charge-based-pricing lived on top of lesser-of-charges and
// were never re-promoted when the rule moved to its own domain. They're kept here
// so any historical rows that still carry them can be read without crashing the
// enum import, but they have no label, color, or badge rendering.
export const CLAIM_CATEGORIES = {
  CHARGE_BELOW_CONTRACT_RATE: "CHARGE_BELOW_CONTRACT_RATE",
  CHARGE_BELOW_CONTRACT_RATE_LINE: "CHARGE_BELOW_CONTRACT_RATE_LINE",
  CHARGE_BELOW_CONTRACT_RATE_TOTAL: "CHARGE_BELOW_CONTRACT_RATE_TOTAL",
  CHARGE_BASED_PRICING_LINE: "CHARGE_BASED_PRICING_LINE",
  CHARGE_BASED_PRICING_TOTAL: "CHARGE_BASED_PRICING_TOTAL",
  // CLE-2326: priced from a contract that doesn't cover the DOS — under/overpayment label
  // is unreliable so the payment-type chip is suppressed in the UI for these rows.
  EFFECTIVE_YEAR_MISSING: "EFFECTIVE_YEAR_MISSING",
  // CLE-2462: radiology claim whose only rate gap is a Technical Component (TC modifier) line —
  // the payer correctly paid the TC portion while our fee schedule modeled the global rate.
  // Still an UNDERPAYMENT by paymentType, but the category tags it as a false positive and it is
  // hidden from the default underpayment worklist (opt back in via the category filter).
  TC_COMPONENT_VARIANT: "TC_COMPONENT_VARIANT",
  // CLE-2489: claim carrying RARC N6 (FEHB 5 U.S.C. 8904(b)) — a 65+ patient without Medicare
  // whose FEHB plan is legally capped at the Medicare-allowed amount. The payer paid correctly at
  // the Medicare rate while our fee schedule modeled the commercial rate, so it is still an
  // UNDERPAYMENT by paymentType but a false positive; hidden from the default underpayment
  // worklist (opt back in via the category filter). NOT a denial.
  MEDICARE_RATE_CAP: "MEDICARE_RATE_CAP",
  // CLE-2836: a professional line allowed AND paid $0 with a global-period denial, where an
  // ADJUDICATED surgery for the same patient in the prior 10 or 90 days opened a CMS global period
  // that still covers this line's date of service. The line is PRICED AT $0 by a claim-side pricing
  // adjustment, so paymentType is whatever the adjusted money says - this category hides nothing.
  // INTERNAL MARKER ONLY: no label, no badge, no filter option (Ani 2026-09-23 — the global period
  // is a normal pricing adjustment). The user-facing surfaces are the $0 line on the Expected
  // Amounts tab and the Global Surgical Period rule in the contract-language panel. NOT a denial.
  GLOBAL_PERIOD_BUNDLED: "GLOBAL_PERIOD_BUNDLED",
} as const;

/** Every category that signals the claim was capped by the lesser-of-charges rule
 *  (or its legacy predecessor). Use this when filtering or rendering "below
 *  contract rate" claims so the legacy and new enum values stay grouped. */
export const CHARGE_BELOW_CONTRACT_RATE_CATEGORIES: string[] = [
  CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE,
  CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_LINE,
  CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_TOTAL,
];

/** CLE-2326: categories signaling the claim was priced off a contract that does not
 *  cover its dateOfService. The under/overpayment label is unreliable for these rows
 *  and should be suppressed in the UI. */
export const EFFECTIVE_YEAR_MISSING_CATEGORIES: string[] = [
  CLAIM_CATEGORIES.EFFECTIVE_YEAR_MISSING,
];

/** CLE-2462: TC-component-variant categories. Grouped like the other worklist-suppressed
 *  buckets so the badge/filter render sites can gate on a single membership check. */
export const TC_COMPONENT_VARIANT_CATEGORIES: string[] = [
  CLAIM_CATEGORIES.TC_COMPONENT_VARIANT,
];

/** CLE-2489: Medicare-rate-cap (FEHB N6) categories. Grouped like the other worklist-suppressed
 *  buckets so the badge/filter render sites can gate on a single membership check. */
export const MEDICARE_RATE_CAP_CATEGORIES: string[] = [
  CLAIM_CATEGORIES.MEDICARE_RATE_CAP,
];

export const CATEGORY_LABELS: Record<string, string> = {
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE]: "Below Contract",
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_LINE]: "Below Contract (line)",
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_TOTAL]: "Below Contract (total)",
  [CLAIM_CATEGORIES.EFFECTIVE_YEAR_MISSING]: "Effective Year Missing",
  [CLAIM_CATEGORIES.TC_COMPONENT_VARIANT]: "TC Component",
  [CLAIM_CATEGORIES.MEDICARE_RATE_CAP]: "Medicare Rate Cap",
  // CHARGE_BASED_PRICING_LINE / CHARGE_BASED_PRICING_TOTAL: intentionally omitted —
  // the new ChargeBasedPricing rule does not produce a badge per PM.
  // GLOBAL_PERIOD_BUNDLED: intentionally omitted too — CLE-2836, Ani 2026-09-23
  // ("yes lets please remove those from panel and view"). The global period is a normal
  // pricing adjustment, so the category is an internal marker with no label and no badge.
};

export const CATEGORY_COLORS: Record<
  string,
  "info" | "success" | "warning" | "danger" | undefined
> = {
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE]: "info",
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_LINE]: "info",
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_TOTAL]: "info",
  // Warning-styled to signal "data quality" rather than a payment-variance bucket —
  // the under/overpayment number on the row is computed off a contract that doesn't
  // cover the DOS and shouldn't drive worklist triage.
  [CLAIM_CATEGORIES.EFFECTIVE_YEAR_MISSING]: "warning",
  // Info-styled like the Below Contract bucket: a real variance against the global rate that
  // is nonetheless a false-positive underpayment (the payer paid the TC portion correctly).
  [CLAIM_CATEGORIES.TC_COMPONENT_VARIANT]: "info",
  // Info-styled like the TC bucket: a real variance against the commercial rate that is a
  // false-positive underpayment (the payer paid correctly at the legally-required Medicare cap).
  [CLAIM_CATEGORIES.MEDICARE_RATE_CAP]: "info",
};

/**
 * Underpayment Analysis Prompt interfaces
 * Versioned AI prompts for claim analysis
 * Note: Name kept as "UnderpaymentAnalysisPrompt" to match unchanged backend table name
 */

export interface IUnderpaymentAnalysisPrompt {
  id: string; // UUID
  version: number; // Sequential version number
  createdAt: string; // ISO 8601 timestamp
  identity: string; // First section: Role/identity description
  issueSummary: string; // Second section: STEP 1-2, implant exclusion, discrepancy detection
  classification: string; // Third section: STEP 3, taxonomy, deterministic rules
  appealLetter: string; // Fourth section: Appeal letter template (JSON schema injected automatically)
  reprocessLetter: string; // Fifth section: Reprocess letter template (optional, may be empty)
  finalRules: string; // Sixth section: Final rules and closing instructions
}

export interface IPromptVersionSummary {
  id: string; // UUID
  version: number; // Sequential version number
  createdAt: string; // ISO 8601 timestamp
}

/**
 * CLE-2702 — Bulk Appeals
 */
export interface IBulkAppealClaimRow {
  claimNumber: string | null;
  serviceDate: string; // ISO 8601 date
  code: string | null; // representative CPT, or DRG-XXX for inpatient
  billedDollars: number;
  expectedDollars: number;
  paidDollars: number;
  underpaymentDollars: number;
}

export interface IBulkAppealGroup {
  classification: string;
  category: string | null;
  payerName: string | null;
  payerId: number | null;
  planLabel: string | null;
  claimCount: number;
  totalUnderpaymentDollars: number;
  avgUnderpaymentDollars: number;
  claims: IBulkAppealClaimRow[];
}

export interface IBulkAppealLetter {
  subject: string;
  body: string;
}

/**
 * CLE-2861 — Reprocessed Claims (GET /claims/reprocessed). Every claim the payer adjudicated more
 * than once: what was allowed originally, what is allowed now, and the difference. Wire values are
 * the API's; the SPA owns the display labels (reprocessedClaims.utils.ts).
 */
export type ReprocessedClaimsView =
  | "all"
  | "clear_health_identified"
  | "historical";

export type ReprocessedRecoveryStatus =
  | "FULLY_RECOVERED"
  | "PARTIALLY_RECOVERED"
  | "NOT_RECOVERED"
  | "UNKNOWN";

export type ReprocessedClaimSource = "CLEAR_HEALTH_IDENTIFIED" | "HISTORICAL";

// The API's sort keys. They are NOT the row field names (e.g. `originalAmount`, not
// `originalAmountInCents`), so each table column sets its `sortField` explicitly.
export type ReprocessedClaimsSortField =
  | "payerName"
  | "claimNumber"
  | "patientName"
  | "originalPaymentDate"
  | "originalAmount"
  | "reprocessedPaymentDate"
  | "finalAmount"
  | "additionalAmount"
  | "paymentIncreasePercent"
  | "recoveryStatus";

export interface IReprocessedClaimRow {
  claimId: string; // episode root id
  claimNumber: string | null;
  patientName: string;
  payerName: string;
  payerId: number | null;
  providerId: string | null;
  providerName: string | null;
  cptCodes: string[];
  serviceDate: string; // YYYY-MM-DD
  originalPaymentDate: string | null; // YYYY-MM-DD; null = pre-dates the remits on file
  originalAmountInCents: number;
  reprocessedPaymentDate: string; // YYYY-MM-DD
  finalAmountInCents: number;
  additionalAmountInCents: number; // final - original, can be negative
  paymentIncreasePercent: number | null; // one decimal; null when the original amount is 0
  recoveryStatus: ReprocessedRecoveryStatus;
  source: ReprocessedClaimSource;
  // The denial classification of the ORIGINAL adjudication, not the current one. All three are
  // null together when the original adjudication has no classification.
  originalClassificationCode: string | null; // e.g. "B10"
  originalClassificationTitle: string | null; // e.g. "Code Replacement / Downgrade"
  originalClassificationReason: string | null; // the classifier's sentence; shown as a tooltip
}

// Covers every row matching the filters and the view, not just the page.
export interface IReprocessedClaimsSummary {
  claimCount: number;
  originalAmountInCents: number;
  finalAmountInCents: number;
  additionalAmountInCents: number; // net, can be negative
  recoveryRatePercent: number | null; // one decimal; null when it cannot be computed
}

export interface IReprocessedClaimsResponse {
  summary: IReprocessedClaimsSummary;
  total: number;
  rows: IReprocessedClaimRow[];
}

export interface IReprocessedClaimsFilters {
  view: ReprocessedClaimsView;
  payerIds: string[]; // payer-id strings or "no-match"
  providerIds: string[];
  startDate?: Date | null;
  endDate?: Date | null;
  recoveryStatuses: ReprocessedRecoveryStatus[];
  // Original denial classification codes (e.g. "B10"), or "none" for rows with no original
  // classification.
  classificationCodes: string[];
  renderingTaxonomies: string[]; // taxonomy codes
  renderingProviderNpis: string[]; // 10-digit NPIs
  page: number; // 1-based
  pageSize: number;
  sortField: ReprocessedClaimsSortField;
  sortOrder: "asc" | "desc";
}

export interface IReprocessedClassificationOption {
  code: string;
  title: string | null;
  count: number;
}

export interface IReprocessedRenderingTaxonomyOption {
  code: string;
  count: number;
}

export interface IReprocessedRenderingProviderOption {
  npi: string;
  name: string | null;
  count: number;
}

// GET /claims/reprocessed/filter-options — computed over the account's whole page population with
// no filters applied. Each array arrives already sorted; the SPA keeps the server's order.
export interface IReprocessedClaimsFilterOptions {
  classifications: IReprocessedClassificationOption[];
  hasUnclassified: boolean; // some rows have no original classification → offer "none"
  renderingTaxonomies: IReprocessedRenderingTaxonomyOption[];
  renderingProviders: IReprocessedRenderingProviderOption[];
}

// POST /claims/reprocessed/backfill answers 202 as soon as the candidates are counted; the scan
// itself finishes in the background, so this is what will be checked, not what changed.
export interface IReprocessedClaimsRescanResponse {
  candidateCount: number;
  scope: "account" | "all";
}

export interface IAppealTemplate {
  id: string; // UUID
  classification: string;
  subject: string;
  body: string;
  isActive: boolean;
  createdAt: string; // ISO 8601 timestamp
  updatedAt: string; // ISO 8601 timestamp
}

/**
 * Variance Analysis interfaces
 * Root cause analysis for discrepancies between 835 claim data and matched estimates
 */

export interface IVarianceAnalysis {
  /** True if any root cause discrepancy was detected */
  hasDiscrepancy: boolean;
  /** Detailed root cause analysis */
  rootCauses: IRootCauses;
}

export interface IRootCauses {
  /** Allowable amount mismatch - claim vs expected (underpayment/overpayment) */
  allowableMismatch: IAllowableMismatchAnalysis | null;
  /** Case signature change between claim and estimate */
  codeChange: ICodeChangeAnalysis | null;
  /** Implant CPT codes present in one but not the other */
  implantPtResponsibility: IImplantPtResponsibilityAnalysis | null;
  /** Benefit component mismatches (deductible, coinsurance %, OOP, copay) */
  benefitsMismatch: IBenefitsMismatchAnalysis | null;
  /** Payer type mismatch between claim (MA) and estimate (Commercial), or vice versa */
  payerTypeMismatch: IPayerTypeMismatchAnalysis | null;
}

export interface IAllowableMismatchAnalysis {
  /** Whether a mismatch was detected */
  detected: boolean;
  /** Type of mismatch: claim_high (claim > expected, overpayment), claim_low (claim < expected, underpayment), or null */
  type: "claim_high" | "claim_low" | null;
  /** Expected allowed amount in cents (from precalculated allowables) */
  expectedAllowed: number;
  /** Claim allowed amount in cents (actual payer payment from 835) */
  claimAllowed: number;
  /** Variance in cents (claim - expected), positive = claim higher than expected */
  variance: number;
}

export interface IQuantityChange {
  /** CPT code */
  code: string;
  /** Quantity in claim */
  claimQty: number;
  /** Quantity in estimate */
  estimateQty: number;
}

export interface ICodeChangeAnalysis {
  /** Whether a code change was detected */
  detected: boolean;
  /** Claim case signature (CPT:qty format) */
  claimSignature: string;
  /** Estimate case signature (CPT:qty format) */
  estimateSignature: string;
  /** CPT codes in claim but not in estimate */
  codesAdded: string[];
  /** CPT codes in estimate but not in claim */
  codesDropped: string[];
  /** CPT codes in both but with different quantities */
  quantityChanges: IQuantityChange[];
}

export interface IImplantPtResponsibilityAnalysis {
  /** Whether an implant discrepancy was detected */
  detected: boolean;
  /** Implant CPT codes in claim but not in estimate */
  implantsInClaimOnly: string[];
  /** Implant CPT codes in estimate but not in claim */
  implantsInEstimateOnly: string[];
}

export interface IBenefitsMismatchAnalysis {
  /** Whether any benefit mismatch was detected */
  detected: boolean;
  /** Deductible mismatch (claim > estimate only) */
  deductible: IBenefitMismatchDetail | null;
  /** Coinsurance percentage mismatch (any direction) */
  coinsurancePercent: ICoinsurancePercentMismatch | null;
  /** OOP max mismatch (claim > estimate only) */
  oopMax: IBenefitMismatchDetail | null;
  /** Copay mismatch (claim > estimate only) */
  copay: IBenefitMismatchDetail | null;
}

export interface IBenefitMismatchDetail {
  /** Whether this specific benefit mismatch was detected */
  detected: boolean;
  /** Claim amount in cents */
  claimAmount: number;
  /** Estimate amount in cents */
  estimateAmount: number;
  /** Variance in cents */
  variance: number;
}

export interface ICoinsurancePercentMismatch {
  /** Whether a coinsurance percentage mismatch was detected */
  detected: boolean;
  /** Claim coinsurance percentage */
  claimPercent: number;
  /** Estimate coinsurance percentage */
  estimatePercent: number;
  /** Variance in percentage points */
  variance: number;
}

export interface IPayerTypeMismatchAnalysis {
  /** Whether a payer type mismatch was detected */
  detected: boolean;
  /** Claim payer type */
  claimPayerType: PayerType | null;
  /** Estimate payer type */
  estimatePayerType: PayerType | null;
  /** Type of mismatch: claim is MA and estimate is Commercial, or vice versa */
  mismatchType:
    | "claim_ma_estimate_commercial"
    | "claim_commercial_estimate_ma"
    | null;
}

/**
 * AI Difference Analysis Result
 * Compares an underpaid claim against similar correctly-paid claims
 */
export interface IDifferenceAnalysisResult {
  /** When analysis was performed (ISO 8601 timestamp) */
  analyzedAt: string;
  /** Plain text analysis explaining why this claim was underpaid */
  analysis: string;
  /** IDs of the similar claims used in the comparison */
  similarClaimIds: string[];
  /** OpenAI model used */
  model: string;
}

/**
 * Similar Claims Search interfaces
 * Used for finding correctly-paid claims as evidence for appeals
 */

/** Summary of a similar correctly-paid claim */
export interface ISimilarClaimSummary {
  /** Unique identifier (UUID) */
  id: string;
  /** Claim number from 835 file */
  claimNumber: string | null;
  /** Date of service (ISO 8601 format) */
  serviceDate: string;
  /** Patient full name */
  patientName: string;
  /** Case signature (CPT:qty format, e.g., "19318:2,64445:1") */
  caseSignature: string;
  /** Actual allowed amount from 835 in cents */
  claimAmount: number;
  /** Expected allowed amount per contract in cents */
  expectedAmount: number | null;
  /** Match percentage (0-100) based on overlapping problem CPT codes */
  matchPercentage: number;
  /** Match tier: 'perfect' for 100%, 'partial' for 50-99% */
  matchTier: "perfect" | "partial";
}

/** Response from similar claims search endpoint */
export interface ISimilarClaimsResponse {
  /** Array of similar correctly-paid claims */
  similarClaims: ISimilarClaimSummary[];
  /** Criteria used to find similar claims */
  matchCriteria: {
    /** CPT codes with underpayment from AI analysis (where actualAllowed < expectedAllowed) */
    problemCptCodes: string[];
    /** Payer name used for matching */
    clearPayerName: string;
  };
  /** Total number of similar claims found */
  totalFound: number;
}
